import { useEffect, useState } from "react";
import { Navigate, NavLink } from "react-router-dom";
import useAdminStore from "./adminStore";
import { getToken, logout } from "./api";
import { isPrinterOnline, orderStatus } from "./adminLogic";

const POLL_MS = 30000;

const TABS = [
    ["/admin/bestellungen", "Bestellungen"],
    ["/admin/reservierungen", "Reservierungen"],
    ["/admin/speisekarten", "Speisekarten"],
    ["/admin/einstellungen", "Einstellungen"],
    ["/admin/anfragen", "Anfragen"],
    ["/admin/galerie", "Galerie"],
];

// Rahmen der Verwaltung: Kopf mit Druckerstatus und Abmelden, Tabs, Inhalt (max. 720 px).
export default function AdminLayout({ children }) {
    const { orders, fetchOrders, printerPing, fetchPrinter } = useAdminStore();
    const [now, setNow] = useState(() => new Date());
    const loggedIn = Boolean(getToken());

    useEffect(() => {
        if (!loggedIn) return undefined;
        const refresh = () => {
            fetchOrders();
            fetchPrinter();
            setNow(new Date());
        };
        refresh();
        const id = setInterval(refresh, POLL_MS);
        return () => clearInterval(id);
    }, [loggedIn, fetchOrders, fetchPrinter]);

    useEffect(() => {
        document.title = "Verwaltung – En Edem Germania";
    }, []);

    if (!loggedIn) {
        return <Navigate to="/login" replace />;
    }

    const printerOnline = isPrinterOnline(printerPing, now);
    const openCount = orders.filter((o) => orderStatus(o) === "offen").length;

    return (
        <div className="site min-h-screen">
            <div className="mx-auto flex min-h-screen max-w-[720px] flex-col">
                <header className="sticky top-0 z-10 border-b border-forest/10 bg-creme/95 backdrop-blur">
                    <div className="flex items-center justify-between px-5 pt-3 pb-1.5">
                        <div className="flex items-center gap-3">
                            <img src="/media/logo.png" alt="" className="h-10 w-auto mix-blend-multiply" />
                            <div className="text-xs font-semibold uppercase tracking-[.2em] text-messing">Verwaltung</div>
                        </div>
                        <div className="flex items-center gap-2.5 text-xs text-forest/60">
                            <span
                                aria-hidden="true"
                                className={`inline-block h-2 w-2 rounded-full ${printerOnline ? "bg-erfolg" : "bg-fehler"}`}
                            />
                            <span>{printerOnline ? "Drucker online" : "Drucker offline"}</span>
                            <button type="button" onClick={logout} className="ml-2.5 bg-transparent py-2 text-forest">
                                Abmelden
                            </button>
                        </div>
                    </div>
                    <nav aria-label="Verwaltung" className="flex gap-1 overflow-x-auto px-3 [scrollbar-width:none]">
                        {TABS.map(([to, label]) => (
                            <NavLink
                                key={to}
                                to={to}
                                className={({ isActive }) =>
                                    `flex min-h-[44px] flex-none items-center gap-1.5 border-b-2 px-2.5 py-3 text-[13px] font-medium no-underline ${
                                        isActive ? "border-forest text-forest" : "border-transparent text-forest/55"
                                    }`
                                }
                            >
                                <span>{label}</span>
                                {to === "/admin/bestellungen" && openCount > 0 && (
                                    <span
                                        aria-label={`${openCount} offen`}
                                        className="rounded-full bg-forest px-[7px] py-px text-[11px] text-creme"
                                    >
                                        {openCount}
                                    </span>
                                )}
                            </NavLink>
                        ))}
                    </nav>
                </header>
                <main className="flex-1 px-5 pt-5 pb-16">{children}</main>
            </div>
        </div>
    );
}
